<template>
  <div class="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-8">
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <div>
        <h3 class="text-lg font-semibold text-black mb-4">
          Upload de fichier Excel
        </h3>
        <p class="text-sm text-black mb-4">
          Choisir votre fichier Excel (.xls ou .xlsx) contenant toutes les
          données de vos produits
        </p>

        <div class="space-y-4">
          <div
            class="border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer"
            :class="
              isDragOver
                ? 'border-blue-400 bg-blue-50'
                : 'border-gray-300 hover:border-blue-400'
            "
            @dragover.prevent="handleDragOver"
            @dragleave.prevent="handleDragLeave"
            @drop.prevent="handleFileDrop"
            @click="triggerFileInput"
          >
            <input
              ref="fileInputRef"
              type="file"
              accept=".xls,.xlsx"
              @change="handleFileChange"
              class="hidden"
            />
            <i class="fas fa-file-excel text-4xl text-green-500 mb-2"></i>
            <p class="text-black">
              {{
                selectedFile
                  ? selectedFile.name
                  : "Glissez-déposez un fichier Excel ou cliquez pour sélectionner"
              }}
            </p>
          </div>

          <div class="flex gap-3">
            <ActionButton
              v-if="selectedFile"
              label="Analyser le fichier"
              icon="fas fa-search"
              variant="secondary"
              @click="$emit('analyze')"
              :loading="isAnalyzing"
              class="flex-1"
            />
          </div>
        </div>
      </div>

      <div class="border-l border-gray-200 pl-8">
        <h3 class="text-lg font-semibold text-black mb-4">Template Excel</h3>
        <p class="text-sm text-black mb-4">
          Note : Vous pouvez télécharger ci-dessous le template Excel pour vos
          futures mises en ligne.
        </p>

        <ActionButton
          label="Template Excel"
          icon="fas fa-file-excel"
          variant="success"
          :loading="isDownloadingTemplate"
          :disabled="isDownloadingTemplate"
          @click="$emit('download-template')"
          class="w-full mb-4"
        />

        <!-- Format colonne Tailles — point le plus souvent oublié -->
        <div class="bg-sky-50 border border-sky-200 rounded-lg p-4 mb-3">
          <h4 class="text-sm font-semibold text-sky-900 mb-2">
            <i class="fas fa-ruler-combined mr-2"></i>Colonne « Tailles »
          </h4>
          <p class="text-xs text-sky-800 mb-2">
            Pour les ventes <strong>par couleurs</strong> ou <strong>mélangées</strong>, écrivez :
          </p>
          <p class="text-sm text-sky-900 font-semibold mb-2">
            <code class="bg-white px-2 py-1 rounded border border-sky-200">1*S,1*M,1*L</code>
            <span class="font-normal text-sky-700 text-xs ml-1">et non</span>
            <code class="bg-white px-2 py-1 rounded border border-sky-200 font-normal text-sky-600 line-through ml-1">S,M,L</code>
          </p>
          <p class="text-xs text-sky-700">
            Une seule taille : <code class="bg-white px-1.5 py-0.5 rounded border border-sky-200">1*TU</code>
            · Vendu par tailles : <code class="bg-white px-1.5 py-0.5 rounded border border-sky-200">S,M,L</code>
          </p>
        </div>

        <!-- Aide pour éviter les erreurs -->
        <div class="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h4 class="text-sm font-semibold text-amber-800 mb-2">
            <i class="fas fa-exclamation-triangle mr-2"></i>Avant d'importer
          </h4>
          <ul class="text-xs text-amber-700 space-y-1">
            <li>
              • Utilisez uniquement les
              <button
                type="button"
                class="font-semibold underline underline-offset-2 hover:text-amber-900"
                @click="openOfficialColorsModal"
              >
                couleurs officielles disponibles
              </button>
            </li>
            <li>
              • Vérifiez que vos <strong>tailles</strong> existent dans "Mes
              déclinaisons"
            </li>
            <li>
              • Vérifiez que vos <strong>packs</strong> existent dans "Gestion
              des paquets"
            </li>
            <li>
              • <strong>Compositions</strong> : format "Coton*80,Polyester*20"
              ou "Coton*46.5,Polyester*53.5" (total = 100%, décimales avec
              point)
            </li>
            <li>
              • <strong>Chaussures</strong> :
              "Tige*Cuir,Doublure*Textile,Semelle*Caoutchouc"
            </li>
            <li>
              • <strong>Références "vendu par" avec couleurs</strong> : si une
              même référence existe en plusieurs quantités de paquet, modifiez
              la référence pour les distinguer (ex : <strong>0000</strong> →
              <strong>0000-p1</strong>, <strong>0000-p6</strong>)
            </li>
            <li>
              • <strong>Stock</strong> : format selon le type de vente —
              <em>par couleur</em> : <code>rouge*10,gris*5</code> |
              <em>couleurs mélangées</em> : <code>10</code> (quantité de paquets) |
              <em>par taille</em> : <code>rouge*S*5,rouge*M*10,gris*S*3,gris*M*8</code> (couleur*taille*quantité)
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Modal couleurs officielles -->
    <Teleport to="body">
      <div
        v-if="showOfficialColorsModal"
        class="fixed inset-0 z-[9999] overflow-hidden flex items-center justify-center p-3 md:p-4"
        @wheel.prevent
      >
        <div
          class="absolute inset-0 bg-black/40"
          @click="showOfficialColorsModal = false"
        ></div>

        <div
          class="relative w-full max-w-3xl bg-white rounded-2xl shadow-xl max-h-[85dvh] overflow-hidden flex flex-col"
          @click.stop
          @wheel.stop
        >
          <div
            class="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 border-b border-gray-200 flex-shrink-0"
          >
            <h3 class="text-base sm:text-lg font-semibold text-black whitespace-nowrap">
              Couleurs officielles
            </h3>
            <div class="relative flex-1 max-w-md ml-auto min-w-0">
              <i
                class="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs"
              ></i>
              <input
                v-model.trim="searchTerm"
                type="text"
                autocomplete="off"
                placeholder="Rechercher une couleur"
                class="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-full focus:outline-none focus:ring-1 focus:ring-black focus:border-black"
              />
            </div>
            <button
              type="button"
              class="text-gray-500 hover:text-black p-2 flex-shrink-0"
              aria-label="Fermer"
              @click="showOfficialColorsModal = false"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>

          <div class="px-4 py-2 border-b border-gray-100 text-xs text-black bg-gray-50 flex-shrink-0">
            Utilisez exactement ces libellés dans votre fichier Excel (colonne couleur).
            <span v-if="!loadingColors">{{ officialColors.length }} couleur(s)</span>
          </div>

          <div class="flex-1 overflow-y-auto overscroll-contain p-4">
            <div v-if="loadingColors" class="py-10 text-center text-sm text-gray-500">
              <i class="fas fa-spinner fa-spin mr-2"></i>
              Chargement des couleurs…
            </div>

            <div
              v-else-if="loadError"
              class="py-10 text-center text-sm text-red-600"
            >
              {{ loadError }}
              <button
                type="button"
                class="block mx-auto mt-2 underline"
                @click="loadOfficialColors"
              >
                Réessayer
              </button>
            </div>

            <div
              v-else-if="visibleFamilies.length === 0"
              class="py-10 text-center text-sm text-gray-500"
            >
              Aucune couleur trouvée
            </div>

            <div v-else class="space-y-5">
              <section
                v-for="family in visibleFamilies"
                :key="family.id"
              >
                <div class="flex items-center gap-2 mb-2">
                  <span
                    class="w-3.5 h-3.5 rounded-full flex-shrink-0 border border-black/10"
                    :style="familySwatchStyle(family)"
                  ></span>
                  <h4 class="text-sm font-semibold text-black">
                    {{ family.label }}
                    <span class="font-normal text-gray-500">
                      ({{ (groups[family.id] || []).length }})
                    </span>
                  </h4>
                </div>
                <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  <div
                    v-for="color in groups[family.id] || []"
                    :key="color.id_couleur"
                    class="flex items-center gap-2 px-2 py-1.5 rounded-md border border-gray-200 bg-white"
                  >
                    <span
                      class="w-4 h-4 rounded-full flex-shrink-0 border border-black/15"
                      :style="getColorSwatchStyle(color.couleur_FR, color.hexColor)"
                    ></span>
                    <span class="text-xs text-black truncate" :title="color.couleur_FR">
                      {{ color.couleur_FR }}
                    </span>
                  </div>
                </div>
              </section>
            </div>
          </div>

          <div class="border-t border-gray-200 px-4 py-3 flex justify-end flex-shrink-0">
            <button
              type="button"
              class="px-4 py-2 text-sm bg-black text-white rounded-lg hover:bg-gray-800"
              @click="showOfficialColorsModal = false"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { ActionButton } from "@/components";
import { getCouleursOfficielles, type CouleurAPILike } from "@/api/couleurs";
import {
  type BasicColorFamily,
  getColorSwatchStyle,
  getFamiliesWithVendorColors,
  groupVendorColorsByBasicFamily,
} from "@/utils/colorFamilies";

interface Props {
  selectedFile: File | null;
  isAnalyzing: boolean;
  isDownloadingTemplate?: boolean;
}

interface Emits {
  (e: "file-selected", file: File): void;
  (e: "analyze"): void;
  (e: "download-template"): void;
}

withDefaults(defineProps<Props>(), { isDownloadingTemplate: false });
const emit = defineEmits<Emits>();

const fileInputRef = ref<HTMLInputElement | null>(null);
const isDragOver = ref(false);

const showOfficialColorsModal = ref(false);
const officialColors = ref<CouleurAPILike[]>([]);
const loadingColors = ref(false);
const loadError = ref<string | null>(null);
const searchTerm = ref("");

const normalize = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const filteredColors = computed(() => {
  const list = officialColors.value;
  if (!searchTerm.value) return list;
  const term = normalize(searchTerm.value);
  return list.filter(
    (c) =>
      normalize(c.couleur_FR || "").includes(term) ||
      normalize(c.couleur_EN || "").includes(term),
  );
});

const groups = computed(() =>
  groupVendorColorsByBasicFamily(filteredColors.value),
);

const visibleFamilies = computed(() =>
  getFamiliesWithVendorColors(filteredColors.value),
);

const familySwatchStyle = (family: BasicColorFamily) => {
  if (family.swatch.startsWith("linear-gradient")) {
    return { backgroundImage: family.swatch };
  }
  return { backgroundColor: family.swatch };
};

const loadOfficialColors = async () => {
  loadingColors.value = true;
  loadError.value = null;
  try {
    officialColors.value = await getCouleursOfficielles();
  } catch (err) {
    loadError.value =
      err instanceof Error
        ? err.message
        : "Impossible de charger les couleurs officielles";
    officialColors.value = [];
  } finally {
    loadingColors.value = false;
  }
};

const openOfficialColorsModal = async () => {
  showOfficialColorsModal.value = true;
  searchTerm.value = "";
  if (!officialColors.value.length && !loadingColors.value) {
    await loadOfficialColors();
  }
};

const triggerFileInput = () => {
  fileInputRef.value?.click();
};

const handleFileChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (target.files && target.files.length > 0) {
    const file = target.files[0];
    if (isValidExcelFile(file)) {
      emit("file-selected", file);
    }
  }
};

const handleDragOver = (event: DragEvent) => {
  event.preventDefault();
  isDragOver.value = true;
};

const handleDragLeave = (event: DragEvent) => {
  event.preventDefault();
  isDragOver.value = false;
};

const handleFileDrop = (event: DragEvent) => {
  event.preventDefault();
  isDragOver.value = false;

  const files = event.dataTransfer?.files;
  if (files && files.length > 0) {
    const file = files[0];
    if (isValidExcelFile(file)) {
      emit("file-selected", file);
    }
  }
};

const isValidExcelFile = (file: File): boolean => {
  const validExtensions = [".xls", ".xlsx"];
  const fileName = file.name.toLowerCase();
  return validExtensions.some((ext) => fileName.endsWith(ext));
};
</script>
